// Generated by tools/export_web_contract.py from contract/analytics.json -- do not edit.

/** The platforms a usage event can come from (`common.platform`). */
export type AnalyticsPlatform = "web" | "android" | "ios";

/** `enums.ask_ai_failure`. */
export type AnalyticsAskAiFailure = "request" | "offline" | "rate_limited" | "plan_limit" | "resting" | "full";

/** `enums.ask_ai_thread_action`. */
export type AnalyticsAskAiThreadAction = "new" | "delete";

/** `enums.checkout_kind`. */
export type AnalyticsCheckoutKind = "plan" | "pack";

/** `enums.dialog`. */
export type AnalyticsDialog = "join_chirp" | "join_code" | "model_wait" | "other_meeting" | "top_up" | "delete_confirm" | "rename_meeting" | "ask_ai" | "notify_offer";

/** `enums.enrollment_failure`. */
export type AnalyticsEnrollmentFailure = "capture" | "save";

/** `enums.flow`. */
export type AnalyticsFlow = "new" | "rerecord";

/** `enums.join_method`. */
export type AnalyticsJoinMethod = "chirp" | "code";

/** `enums.link`. */
export type AnalyticsLink = "privacy" | "terms" | "support" | "fair_use" | "store_subscriptions";

/** `enums.meeting_outcome`. */
export type AnalyticsMeetingOutcome = "stopped" | "left" | "discarded" | "setup_cancelled";

/** `enums.notes_style`. */
export type AnalyticsNotesStyle = "meeting" | "client_call" | "interview" | "lecture" | "workshop" | "one_to_one";

/** `enums.notes_view`. */
export type AnalyticsNotesView = "list" | "map";

/** `enums.other_meeting_action`. */
export type AnalyticsOtherMeetingAction = "end" | "leave";

/** `enums.permission_answer`. */
export type AnalyticsPermissionAnswer = "granted" | "declined";

/** `enums.plan`. */
export type AnalyticsPlan = "basic" | "plus" | "pro";

/** `enums.request_failure`. */
export type AnalyticsRequestFailure = "request" | "recording";

/** `enums.result_action_state`. */
export type AnalyticsResultActionState = "checked" | "unchecked";

/** `enums.result_rename_target`. */
export type AnalyticsResultRenameTarget = "meeting" | "speaker";

/** `enums.result_tab`. */
export type AnalyticsResultTab = "quick" | "detailed" | "transcript";

/** `enums.retry_surface`. */
export type AnalyticsRetrySurface = "boot" | "account_link" | "model_load" | "result_load" | "result_processing" | "notes_write" | "chat_reply";

/** `enums.screen`. */
export type AnalyticsScreen = "welcome" | "signin" | "app_signin" | "consent" | "enroll" | "rerecord" | "home" | "session" | "result" | "sessions" | "account" | "delete_account" | "plans" | "credits_history" | "settings" | "email_unsubscribe" | "not_found" | "other";

/** `enums.sign_in_failure`. */
export type AnalyticsSignInFailure = "request" | "provider";

/** `enums.sign_in_method`. */
export type AnalyticsSignInMethod = "magic_link" | "google" | "apple" | "code";

/** `enums.theme`. */
export type AnalyticsTheme = "espresso" | "original" | "honey" | "sage" | "navy" | "terracotta" | "aubergine" | "rose";

/** `enums.toggle_state`. */
export type AnalyticsToggleState = "on" | "off";

/** `enums.welcome_control`. */
export type AnalyticsWelcomeControl = "continue" | "next" | "back" | "get_started" | "play_again";

/** Each event the web may send, and its own properties. */
export type AnalyticsEventProperties = {
  screen_viewed: { screen: AnalyticsScreen };
  dialog_viewed: { dialog: AnalyticsDialog };
  dialog_dismissed: { dialog: AnalyticsDialog };
  enrollment_started: { flow: AnalyticsFlow };
  enrollment_step_completed: { flow: AnalyticsFlow; step: 1 | 2 | 3 };
  enrollment_completed: { flow: AnalyticsFlow };
  enrollment_failed: { flow: AnalyticsFlow; category: AnalyticsEnrollmentFailure };
  sign_in_started: { method: AnalyticsSignInMethod };
  sign_in_completed: { method: AnalyticsSignInMethod };
  sign_in_failed: { method: AnalyticsSignInMethod; category: AnalyticsSignInFailure };
  session_create_started: Record<string, never>;
  session_created: Record<string, never>;
  session_create_failed: { category: AnalyticsRequestFailure };
  join_method_selected: { join_method: AnalyticsJoinMethod };
  session_joined: { join_method: AnalyticsJoinMethod };
  session_join_failed: { join_method: AnalyticsJoinMethod; category: AnalyticsRequestFailure };
  meeting_ended: { outcome: AnalyticsMeetingOutcome };
  meeting_nearby_toggled: { state: AnalyticsToggleState };
  notifications_toggled: { state: AnalyticsToggleState };
  other_meeting_resolved: { action: AnalyticsOtherMeetingAction };
  retry_tapped: { surface: AnalyticsRetrySurface };
  result_tab_selected: { tab: AnalyticsResultTab };
  result_exported: Record<string, never>;
  result_renamed: { target: AnalyticsResultRenameTarget };
  result_action_toggled: { state: AnalyticsResultActionState };
  result_notes_style_selected: { style: AnalyticsNotesStyle };
  result_notes_view_selected: { view: AnalyticsNotesView };
  ask_ai_opened: Record<string, never>;
  ask_ai_question_asked: Record<string, never>;
  ask_ai_failed: { category: AnalyticsAskAiFailure };
  ask_ai_thread_cleared: { action: AnalyticsAskAiThreadAction };
  plan_selected: { plan: AnalyticsPlan };
  checkout_started: { kind: AnalyticsCheckoutKind };
  subscription_manage_opened: Record<string, never>;
  account_signed_out: Record<string, never>;
  account_delete_started: Record<string, never>;
  account_deleted: Record<string, never>;
  email_meetings_unsubscribed: Record<string, never>;
  ai_consent_agreed: Record<string, never>;
  theme_selected: { theme: AnalyticsTheme };
  email_meetings_toggled: { state: AnalyticsToggleState };
  link_opened: { link: AnalyticsLink };
  welcome_control_tapped: { control: AnalyticsWelcomeControl; step: 0 | 1 | 2 | 3 | 4 | 5 };
  notification_permission_answered: { answer: AnalyticsPermissionAnswer };
};

export type AnalyticsEventName = keyof AnalyticsEventProperties;

export type AnalyticsPropertySpec =
  | { readonly enum: readonly string[] }
  | { readonly integer: { readonly minimum: number; readonly maximum: number } };

/** Each web event's own properties and their allowed values: the allowlist's source. */
export const ANALYTICS_EVENTS: Readonly<Record<AnalyticsEventName, Readonly<Record<string, AnalyticsPropertySpec>>>> = {
  screen_viewed: {
    screen: { enum: ["welcome", "signin", "app_signin", "consent", "enroll", "rerecord", "home", "session", "result", "sessions", "account", "delete_account", "plans", "credits_history", "settings", "email_unsubscribe", "not_found", "other"] },
  },
  dialog_viewed: {
    dialog: { enum: ["join_chirp", "join_code", "model_wait", "other_meeting", "top_up", "delete_confirm", "rename_meeting", "ask_ai", "notify_offer"] },
  },
  dialog_dismissed: {
    dialog: { enum: ["join_chirp", "join_code", "model_wait", "other_meeting", "top_up", "delete_confirm", "rename_meeting", "ask_ai", "notify_offer"] },
  },
  enrollment_started: {
    flow: { enum: ["new", "rerecord"] },
  },
  enrollment_step_completed: {
    flow: { enum: ["new", "rerecord"] },
    step: { integer: { minimum: 1, maximum: 3 } },
  },
  enrollment_completed: {
    flow: { enum: ["new", "rerecord"] },
  },
  enrollment_failed: {
    flow: { enum: ["new", "rerecord"] },
    category: { enum: ["capture", "save"] },
  },
  sign_in_started: {
    method: { enum: ["magic_link", "google", "apple", "code"] },
  },
  sign_in_completed: {
    method: { enum: ["magic_link", "google", "apple", "code"] },
  },
  sign_in_failed: {
    method: { enum: ["magic_link", "google", "apple", "code"] },
    category: { enum: ["request", "provider"] },
  },
  session_create_started: {},
  session_created: {},
  session_create_failed: {
    category: { enum: ["request", "recording"] },
  },
  join_method_selected: {
    join_method: { enum: ["chirp", "code"] },
  },
  session_joined: {
    join_method: { enum: ["chirp", "code"] },
  },
  session_join_failed: {
    join_method: { enum: ["chirp", "code"] },
    category: { enum: ["request", "recording"] },
  },
  meeting_ended: {
    outcome: { enum: ["stopped", "left", "discarded", "setup_cancelled"] },
  },
  meeting_nearby_toggled: {
    state: { enum: ["on", "off"] },
  },
  notifications_toggled: {
    state: { enum: ["on", "off"] },
  },
  other_meeting_resolved: {
    action: { enum: ["end", "leave"] },
  },
  retry_tapped: {
    surface: { enum: ["boot", "account_link", "model_load", "result_load", "result_processing", "notes_write", "chat_reply"] },
  },
  result_tab_selected: {
    tab: { enum: ["quick", "detailed", "transcript"] },
  },
  result_exported: {},
  result_renamed: {
    target: { enum: ["meeting", "speaker"] },
  },
  result_action_toggled: {
    state: { enum: ["checked", "unchecked"] },
  },
  result_notes_style_selected: {
    style: { enum: ["meeting", "client_call", "interview", "lecture", "workshop", "one_to_one"] },
  },
  result_notes_view_selected: {
    view: { enum: ["list", "map"] },
  },
  ask_ai_opened: {},
  ask_ai_question_asked: {},
  ask_ai_failed: {
    category: { enum: ["request", "offline", "rate_limited", "plan_limit", "resting", "full"] },
  },
  ask_ai_thread_cleared: {
    action: { enum: ["new", "delete"] },
  },
  plan_selected: {
    plan: { enum: ["basic", "plus", "pro"] },
  },
  checkout_started: {
    kind: { enum: ["plan", "pack"] },
  },
  subscription_manage_opened: {},
  account_signed_out: {},
  account_delete_started: {},
  account_deleted: {},
  email_meetings_unsubscribed: {},
  ai_consent_agreed: {},
  theme_selected: {
    theme: { enum: ["espresso", "original", "honey", "sage", "navy", "terracotta", "aubergine", "rose"] },
  },
  email_meetings_toggled: {
    state: { enum: ["on", "off"] },
  },
  link_opened: {
    link: { enum: ["privacy", "terms", "support", "fair_use", "store_subscriptions"] },
  },
  welcome_control_tapped: {
    control: { enum: ["continue", "next", "back", "get_started", "play_again"] },
    step: { integer: { minimum: 0, maximum: 5 } },
  },
  notification_permission_answered: {
    answer: { enum: ["granted", "declined"] },
  },
};

/** The shapes `app_version` and `os_version` must have on every event. */
export const ANALYTICS_COMMON_PATTERNS = {
  app_version: new RegExp("^[0-9A-Za-z][0-9A-Za-z._+-]{0,39}$"),
  os_version: new RegExp("^(iOS|iPadOS|Android|macOS|Windows|Linux|ChromeOS|other)( [0-9]{1,3})?$"),
} as const;
